<script setup lang="ts">
	import { inject, ref } from 'vue'

	import { Hotel } from '../interfaces/hotel'

	interface Props {
		hotel: Hotel
		selectedHotel?: Hotel
		centerActiveHotel: (hotel: Hotel) => void
		getNightFare: (hotel: Hotel) => string
	}

	const props = defineProps<Props>()

	const currencyDailyRate = ref(false)

	const changeCurrency = () => {
		currencyDailyRate.value = !currencyDailyRate.value
	}

	const changeDailyRate = () => {
		if (currencyDailyRate.value) {
			return `$${props.hotel.dailyRate}${props.hotel.currency}`
		} else {
			return `$${props.hotel.dailyRateApproximate} ${props.hotel.requestedCurrency}`
		}
	}

	const isSelected = () => props.selectedHotel?.id === props.hotel.id
</script>

<template>
	<div
		:ref="hotel.id === selectedHotel?.id ? 'activeHotelMarker' : ''"
		class="relative hover:z-50 group"
	>
		<!-- Main marker -->
		<div
			class="relative cursor-pointer transform transition-all duration-300 ease-out"
			:class="{
				'scale-110 -translate-y-1 animate-bounce ': isSelected(),
				'hover:scale-105 hover:-translate-y-0.5 ': !isSelected(),
			}"
			@click="centerActiveHotel(hotel)"
		>
			<!-- Price tag with modern styling -->
			<figure
				:class="[
					'price-tag relative z-10 mx-auto rounded-2xl border-2 transition-all duration-300 ease-out shadow-lg',
					isSelected() ?
						'border-blue-500 bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-xl '
					:	'border-white bg-white text-gray-900 shadow-gray-200/50 hover:border-blue-300 hover:shadow-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-blue-100',
				]"
			>
				<!-- Main content -->
				<div class="px-3 py-2">
					<div class="flex items-center justify-center gap-2">
						<!-- Currency icon -->
						<div
							:class="[
								'w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 transform',
								isSelected() ?
									'bg-blue-100 text-blue-800'
								:	'bg-green-100 text-green-700',
								currencyDailyRate ?
									'scale-110 rotate-12'
								:	'scale-100 rotate-0',
							]"
						>
							$
						</div>

						<!-- Price display  -->
						<span
							:class="[
								'text-sm font-bold whitespace-nowrap leading-none transition-all duration-300',
								'text-current',
							]"
						>
							{{ changeDailyRate() }}
						</span>

						<!-- Currency exchange button -->
						<button
							v-if="hotel?.currency !== hotel?.requestedCurrency"
							@click.stop="changeCurrency"
							:class="[
								'w-7 h-7 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-125 active:scale-95 ring-2',
								isSelected() ?
									'bg-blue-100 text-blue-800 hover:bg-blue-200 ring-blue-300 shadow-lg'
								:	'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700 ring-blue-300 shadow-lg',
							]"
							v-tooltip="
								currencyDailyRate ?
									`Cambiar a ${hotel?.requestedCurrency} (Aprox)`
								:	`Cambiar a ${hotel?.currency} (Original)`
							"
						>
							<span
								:class="[
									'material-symbols-outlined text-sm transition-transform duration-300 font-bold',
									currencyDailyRate ? 'rotate-180' : 'rotate-0',
								]"
							>
								currency_exchange
							</span>
						</button>
					</div>
				</div>

				<!-- Pointer triangle for selected state -->
				<div
					v-if="isSelected()"
					class="absolute -bottom-[18px] left-1/2 transform -translate-x-1/2"
				>
					<div
						class="w-0 h-0 border-l-[20px] border-r-[20px] border-t-[20px] border-transparent border-t-blue-500 drop-shadow-lg"
					></div>
				</div>
			</figure>
		</div>
	</div>
</template>

<style scoped>
	/* Animación de salto personalizada más suave */
	@keyframes gentle-bounce {
		0%,
		100% {
			transform: translateY(0) scale(1.1);
		}
		50% {
			transform: translateY(-8px) scale(1.1);
		}
	}

	/* Aplicar la animación personalizada cuando está seleccionado */
	.animate-gentle-bounce {
		animation: gentle-bounce 2s ease-in-out infinite;
	}

	/* Asegurar que las transiciones funcionen suavemente */
	.price-tag {
		will-change: transform;
	}

	/* Animación de rotación para el icono de intercambio */
	.rotate-180 {
		transform: rotate(180deg);
	}

	/* Efecto de escala en el icono de moneda */
	.scale-110 {
		transform: scale(1.1);
	}

	.rotate-12 {
		transform: rotate(12deg) scale(1.1);
	}

	/* Efecto de resplandor para el botón activo */
	.animate-pulse {
		animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
	}
</style>
